// (c) chaehongee. Confidential and proprietary.

import { useCallback, useMemo, useRef, useState } from 'react'
import type { SnapshotData } from './types'

interface ActionStep {
    before: SnapshotData
    after: SnapshotData
}

function cloneSnapshot(s: SnapshotData): SnapshotData {
    return {
        worldRectsById: JSON.parse(JSON.stringify(s.worldRectsById || {})),
        frameEditState: JSON.parse(JSON.stringify(s.frameEditState || {})),
        initialWorldRectsById: s.initialWorldRectsById ? JSON.parse(JSON.stringify(s.initialWorldRectsById)) : undefined,
    }
}

function snapshotsEqual(a: SnapshotData, b: SnapshotData): boolean {
    const aKeys = Object.keys(a.worldRectsById)
    const bKeys = Object.keys(b.worldRectsById)
    if (aKeys.length !== bKeys.length) return false
    for (const k of aKeys) {
        const ar = a.worldRectsById[k]
        const br = b.worldRectsById[k]
        if (!br) return false
        if (ar.x !== br.x || ar.y !== br.y || ar.w !== br.w || ar.h !== br.h) return false
    }

    const afeKeys = Object.keys(a.frameEditState)
    const bfeKeys = Object.keys(b.frameEditState)
    if (afeKeys.length !== bfeKeys.length) return false
    for (const k of afeKeys) {
        const ae = a.frameEditState[k]
        const be = b.frameEditState[k]
        if (!be) return false
        const compareRect = (r1: { x: number; y: number; w: number; h: number }, r2: { x: number; y: number; w: number; h: number }) =>
            r1.x === r2.x && r1.y === r2.y && r1.w === r2.w && r1.h === r2.h
        if (!compareRect(ae.baseFrame, be.baseFrame)) return false
        if (!compareRect(ae.currentFrame, be.currentFrame)) return false
        
        if (ae.layers.length !== be.layers.length) return false
        for (let i = 0; i < ae.layers.length; i++) {
            const al = ae.layers[i]
            const bl = be.layers[i]
            if (al.id !== bl.id || al.src !== bl.src || al.zIndex !== bl.zIndex) return false
            if (!compareRect(al.baseRect, bl.baseRect)) return false
            if (!compareRect(al.currentRect, bl.currentRect)) return false
        }
    }

    const aiKeys = Object.keys(a.initialWorldRectsById || {})
    const biKeys = Object.keys(b.initialWorldRectsById || {})
    if (aiKeys.length !== biKeys.length) return false
    for (const k of aiKeys) {
        const ar = a.initialWorldRectsById?.[k]
        const br = b.initialWorldRectsById?.[k]
        if (!ar || !br) return false
        if (ar.x !== br.x || ar.y !== br.y || ar.w !== br.w || ar.h !== br.h) return false
    }
    return true
}

interface UseCanvasHistoryOptions {
    getSnapshot: () => SnapshotData
    applySnapshot: (snap: SnapshotData) => void
    capacity?: number
}

export function useCanvasHistory(options: UseCanvasHistoryOptions) {
    const { getSnapshot, applySnapshot, capacity = 100 } = options
    const [past, setPast] = useState<ActionStep[]>([])
    const [future, setFuture] = useState<ActionStep[]>([])
    const startRef = useRef<SnapshotData | null>(null)

    const canUndo = past.length > 0
    const canRedo = future.length > 0

    const beginCapture = useCallback(() => {
        startRef.current = cloneSnapshot(getSnapshot())
    }, [getSnapshot])

    const commitIfChanged = useCallback(() => {
        const start = startRef.current
        startRef.current = null
        if (!start) return
        const end = cloneSnapshot(getSnapshot())
        if (snapshotsEqual(start, end)) return
        const step: ActionStep = { before: start, after: end }
        setPast((p) => {
            const next = [...p, step]
            if (next.length > capacity) next.shift()
            return next
        })
        setFuture([])
    }, [getSnapshot, capacity])

    const clear = useCallback(() => {
        startRef.current = null
        setPast([])
        setFuture([])
    }, [])

    const undo = useCallback(() => {
        if (past.length === 0) return false
        const step = past[past.length - 1]
        applySnapshot(cloneSnapshot(step.before))
        setPast((p) => p.slice(0, p.length - 1))
        setFuture((f) => [step, ...f])
        return true
    }, [past, applySnapshot])

    const redo = useCallback(() => {
        if (future.length === 0) return false
        const [step, ...rest] = future
        applySnapshot(cloneSnapshot(step.after))
        setFuture(rest)
        setPast((p) => [...p, step])
        return true
    }, [future, applySnapshot])

    return useMemo(() => ({
        beginCapture,
        commitIfChanged,
        undo,
        redo,
        canUndo,
        canRedo,
        clear,
    }), [beginCapture, commitIfChanged, undo, redo, canUndo, canRedo, clear])
}

